import { createLogger } from '@/lib/log/client'
import type { PageType } from '@/types/nav-types'

import { buildCompactAffiliateTrackingId } from './affiliate-tracking'
import {
  encodeExperimentAssignments,
  pageExperimentAssignments,
  type ExperimentAssignments,
} from './experiment-tracking'
import { applyNetwork, getNetworkTrackingConfig } from './networks'

import { normalizeUrl } from '../utils/normalize-url'

const log = createLogger('utmize')

// Preserve the visitor's local calendar date without loading a date library
// into every affiliate button. Server callers supply their cached date stamp.
const localDateStamp = () => {
  const date = new Date()
  return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`
}

interface UtmizeProps {
  url: string
  medium?: string
  campaign?: string
  term?: string
  content?: string
  release?: string
  segment?: string
  search?: string
  pathname: string
  featureFlags?: Record<string, string | boolean>
  pageType?: PageType
  sessionId?: string | null
  experiments?: ExperimentAssignments
  components?: readonly string[]
  // YYYYMMDD date stamp for the d= UTM param. Server-side callers pass a
  // build-time constant since `new Date()` is
  // forbidden in server components under Cache Components without a
  // dynamic-data anchor. Client-side callers omit it and get today's date.
  dateStamp?: string
}

export const utmize = ({
  url,
  medium = 'button',
  campaign,
  term = 'lead-term',
  content,
  release,
  segment,
  search,
  pathname,
  featureFlags,
  pageType,
  sessionId,
  experiments,
  components,
  dateStamp,
}: UtmizeProps): string => {
  try {
    const newUrl = new URL(normalizeUrl({ url, origin: content || 'utmize' }))
    const pathWithSlash = pathname.startsWith('/') ? pathname : `/${pathname}`
    const networkConfig = getNetworkTrackingConfig(newUrl.hostname)
    const useCompactTracking = medium === 'button' && networkConfig.compactTracking
    const assignments =
      experiments ?? pageExperimentAssignments({ pathname: pathWithSlash, pageType, segment, components })

    const utmSearchParts: string[] = []
    let trackingId: string | undefined

    if (useCompactTracking) {
      trackingId = buildCompactAffiliateTrackingId(
        {
          pathname: pathWithSlash,
          position: content,
          productSlug: release,
          pageType,
          sessionId,
          experiments: assignments,
        },
        networkConfig.maxTrackingLength
      )
    } else {
      utmSearchParts.push(`s=${pathWithSlash.split('?')[0]}`)
      if (segment) {
        utmSearchParts.push(`v=${segment}`)
      }
      const experimentTokens = encodeExperimentAssignments(assignments)
      if (experimentTokens) {
        utmSearchParts.push(`e=${experimentTokens}`)
      }
      if (campaign) {
        utmSearchParts.push(`ca=${campaign}`)
      }
      utmSearchParts.push(`d=${dateStamp ?? localDateStamp()}`)
      if (featureFlags) {
        const flags = Object.entries(featureFlags)
          .filter(([, v]) => v === 'on')
          .map(([k]) => k.split('_').slice(-1)[0])
        if (flags.length) {
          utmSearchParts.push(`f=${flags.join(';')}`)
        }
      }
      if (medium) {
        utmSearchParts.push(`m=${medium}`)
      }
      if (release) {
        utmSearchParts.push(`r=${release}`)
      }
      const utmSource = search ? new URLSearchParams(search).get('utm_source') : null
      if (utmSource) {
        utmSearchParts.push(`u=${utmSource}`)
      }
    }
    const utmSearch = utmSearchParts.join('|')

    applyNetwork(newUrl, {
      content,
      term,
      pathname: pathWithSlash,
      utmSearch,
      trackingId,
    })

    return newUrl.toString()
  } catch (ex) {
    if (process.env.NODE_ENV === 'development') {
      log.error('bad url: %s', url)
      log.error(ex)
    }
    return url
  }
}
